import {Component, effect, inject, input, NgZone, OnDestroy, signal, ViewChild, ViewEncapsulation} from '@angular/core';

import {DomSanitizer, SafeStyle} from '@angular/platform-browser';
import {Subscription} from 'rxjs';
import {MapHostDirective} from '../../../../directives/map-host.directive';
import {ModalsControlService} from '../../../../services/modals-control.service';
import {ToolsService} from '../../../../services/tools.service';
import {ModalCommand} from '../../../../types/ModalCommand';
import {ModalTypeEnum} from '../../../../enums/ModalEnums';
import {finalize} from 'rxjs/operators';
import {LoaderService} from '../../../../services/loader.service';
import {SearchState} from '../../../../types/SearchState';
import {MapsService} from '../../../../services/maps.service';
import {LoaderQueueItem} from '../../../../types/LoaderQueueItem';
import {CareproviderMapItemTranslated} from '../../../../types/CareproviderMapItemTranslated';
import {CareproviderMapItem} from '../../../../types/CareproviderMapItem';
import {AppConfigService} from '../../../../services/app-config.service';
import {SearchService} from '../../../../services/search/search.service';
import {CareproviderUnitImages} from '../../../../types/CareproviderUnitImages';
import {CareproviderAddress} from '../../../../types/CareproviderAddress';

@Component({
  selector: 'app-google-map-loader',
  templateUrl: './google-map-loader.component.html',
  styleUrls: ['./google-map-loader.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GoogleMapLoaderComponent implements OnDestroy {
  @ViewChild(MapHostDirective, {static: true}) mapHost: MapHostDirective;

  public height = input<number>(null);
  public id = input<number>(null);
  public name = input<string>(null);
  public urlPath = input<string>(null);
  public address = input<CareproviderAddress>(null);
  public caregiverUnitImages = input<CareproviderUnitImages>(null);

  public useStaticData = input<boolean>(false);
  public lazyLoadDynamicMap = input<boolean>(false);

  public markers = signal<CareproviderMapItemTranslated[]>([]);
  public showStaticMap = signal<boolean>(true);
  public staticMapImgSrc = signal<SafeStyle>(null);
  public isLoading = signal<boolean>(false);

  private subscriptions: Subscription[] = [];
  private loadedStaticMapMarkers = signal<CareproviderMapItemTranslated[]>([]);
  private initializedState = signal<SearchState>(null);

  private searchService = inject(SearchService);
  public searchState = this.searchService.searchState;
  private userLocationState = this.searchService.userLocationState;

  constructor(
    private mapsService: MapsService,
    private tools: ToolsService,
    private sanitizer: DomSanitizer,
    private modalControl: ModalsControlService,
    private loaderService: LoaderService,
    private appConfigService: AppConfigService,
    private ngZone: NgZone,
  ) {
    effect(
      () => {
        if (this.searchState()) {
          if (this.lazyLoadDynamicMap() === true) {
            this.ngZone.runOutsideAngular(() => {
              setTimeout(() => {
                this.ngZone.run(() => {
                  this.initDynamicMap();
                });
              }, 5000);
            });
          }
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (this.useStaticData() !== false) {
          if (this.id() && this.name() && this.urlPath() && this.address()) {
            this.loadCareproviderUnitMarker();
          }
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (
          this.useStaticData() === true &&
          !this.isLoading() &&
          JSON.stringify(this.markers()) !== JSON.stringify(this.loadedStaticMapMarkers())
        ) {
          this.loadStaticMapImage();
        }
      },
      {allowSignalWrites: true},
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public openMapModal(): void {
    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.GOOGLE_MAP,
      data: {height: 80, markers: this.markers()},
    };
    this.modalControl.openModal(modalCommand);
  }

  private initDynamicMap(): void {
    if (this.isStateChangeMatterForMap(this.searchState())) {
      this.initializedState.set(this.tools.clone(this.searchState()));
      const queryId: number = this.loaderService.generateQueueId();
      const queryItem: LoaderQueueItem = {
        id: queryId,
        title: '',
        description: '',
      };
      this.loaderService.addToQueue(queryItem);
      this.subscriptions.push(
        this.mapsService
          .GetSearchMapResults(this.searchState(), this.userLocationState())
          .pipe(
            finalize(() => {
              this.loaderService.removeFromQueue(queryId);
            }),
          )
          .subscribe(
            (result) => this.loadMarkers(result),
            (error) => console.error(error),
          ),
      );
    }
  }

  private isStateChangeMatterForMap(state: SearchState): boolean {
    const conditions: boolean[] = [];
    conditions.push(this.initializedState() == null);
    if (this.initializedState() != null) {
      conditions.push(state.caregiverUnitTypeUrlPath !== this.initializedState().caregiverUnitTypeUrlPath);
      conditions.push(state.careServiceTypeUrlPath !== this.initializedState().careServiceTypeUrlPath);
      conditions.push(state.placeUrlPath !== this.initializedState().placeUrlPath);
      conditions.push(state.freeSearchTerm !== this.initializedState().freeSearchTerm);
      conditions.push(state.sort !== this.initializedState().sort);
      conditions.push(state.locationSearch !== this.initializedState().locationSearch);
      conditions.push(state.lat !== this.initializedState().lat);
      conditions.push(state.lon !== this.initializedState().lon);
      conditions.push(state.openNow !== this.initializedState().openNow);
      conditions.push(state.openWeekend !== this.initializedState().openWeekend);
      conditions.push(state.customerFeatureType !== this.initializedState().customerFeatureType);
    }
    return conditions.findIndex((item) => item === true) >= 0;
  }

  private loadStaticMapImage(): void {
    this.isLoading.set(true);
    this.subscriptions.push(
      this.mapsService.GetStaticMap(this.markers(), 2175, 1750).subscribe(
        (result) => this.processStaticMapResult(result),
        (error) => {
          console.error(error);
          this.isLoading.set(false);
        },
      ),
    );
  }

  private processStaticMapResult(staticMapImgSrc: string): void {
    this.isLoading.set(false);
    this.staticMapImgSrc.set(
      this.sanitizer.bypassSecurityTrustStyle(
        'url(' + this.appConfigService.appConfig.amazonS3Url + staticMapImgSrc + ')',
      ),
    );
    this.loadedStaticMapMarkers.set(this.tools.clone(this.markers()));
  }

  private loadMarkers(searchResults: CareproviderMapItem[]): void {
    this.showStaticMap.set(true);
    this.mapHost.viewContainerRef.clear();
    this.markers.set([]);
    searchResults.forEach((item) => {
      this.markers.update((prev) => [
        ...prev,
        {
          id: item.id,
          name: item.name,
          urlPath: item.urlPath,
          location: {
            lat: item.location.lat,
            lng: item.location.lon,
          },
          rating: item.rating,
          address: item.address,
          thumbnail: item.thumbnail,
        },
      ]);
    });
  }

  private loadCareproviderUnitMarker(): void {
    this.showStaticMap.set(true);
    this.mapHost.viewContainerRef.clear();
    this.markers.set([]);
    let imageUrl: string = null;
    if (!this.tools.isNil(this.caregiverUnitImages()) && !this.tools.isNil(this.caregiverUnitImages().thumbnailUrl)) {
      imageUrl = this.caregiverUnitImages().thumbnailUrl;
    }
    this.markers.update((prev) => [
      ...prev,
      {
        id: this.id(),
        name: this.name(),
        urlPath: this.urlPath(),
        location: {
          lat: this.address().latitude,
          lng: this.address().longitude,
        },
        rating: 0,
        address: this.address(),
        thumbnail: imageUrl,
      },
    ]);
  }
}
